<template>
  <div class="exam-page min-h-screen bg-neutral-50 py-8">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <!-- 页面标题 -->
      <div
        class="flex flex-col md:flex-row justify-between items-center mb-8 gap-4"
      >
        <div>
          <h1 class="text-2xl font-bold text-neutral-900">工具审核</h1>
          <p class="text-neutral-500 mt-1">
            审批用户提交的 AI 工具，维护平台内容质量
          </p>
        </div>

        <div class="flex items-center gap-4">
          <!-- 状态筛选 Tab -->
          <div
            class="bg-white p-1 rounded-lg border border-neutral-200 flex text-sm font-medium"
          >
            <button
              v-for="tab in tabs"
              :key="tab.value"
              @click="changeStatus(tab.value)"
              :class="[
                'px-4 py-2 rounded-md transition-all',
                currentStatus === tab.value
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50',
              ]"
            >
              {{ tab.label }}
            </button>
          </div>

          <div
            class="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border border-neutral-200"
          >
            <span class="text-sm text-neutral-500"
              >共
              <span class="font-bold text-neutral-900">{{ total }}</span>
              条</span
            >
            <button
              @click="fetchData"
              class="text-neutral-400 hover:text-primary transition-colors ml-2"
              title="刷新列表"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- 列表区域 -->
      <div v-loading="loading" class="space-y-4">
        <div v-if="list.length > 0" class="space-y-4">
          <div
            v-for="tool in list"
            :key="tool.id"
            class="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div class="flex flex-col sm:flex-row gap-6">
              <!-- 工具封面 -->
              <div class="flex-shrink-0 w-full sm:w-32 h-32 sm:h-32">
                <img
                  :src="tool.cover"
                  :alt="tool.title"
                  class="w-full h-full object-cover rounded-lg border border-neutral-100 cursor-pointer"
                  @click="openDetail(tool)"
                />
              </div>

              <!-- 工具信息 -->
              <div class="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div class="flex justify-between items-start">
                    <h3
                      class="text-lg font-bold text-neutral-900 mb-1 cursor-pointer hover:text-primary transition-colors"
                      @click="openDetail(tool)"
                    >
                      {{ tool.title }}
                    </h3>
                    <span
                      class="text-xs px-2 py-1 rounded-full"
                      :class="{
                        'bg-yellow-100 text-yellow-700': currentStatus === 0,
                        'bg-green-100 text-green-700': currentStatus === 1,
                        'bg-red-100 text-red-700': currentStatus === 2,
                      }"
                    >
                      {{ getStatusText(currentStatus) }}
                    </span>
                  </div>
                  <div class="flex items-center text-sm text-neutral-500 mb-2">
                    <span class="mr-4"
                      ><i class="fa fa-folder-o mr-1"></i
                      >{{ getCategoryName(tool.categoryid) }}</span
                    >
                    <!-- <span class="mr-4"
                      ><i class="fa fa-user-o mr-1"></i
                      >{{ tool.userName || "未知用户" }}</span
                    > -->
                    <span
                      ><i class="fa fa-clock-o mr-1"></i
                      >{{ formatDate(tool.createTime) }}</span
                    >
                  </div>
                  <p class="text-neutral-600 text-sm line-clamp-2 mb-3">
                    {{ tool.description }}
                  </p>
                </div>

                <!-- 附件信息 -->
                <div
                  v-if="tool.fileId"
                  class="flex items-center text-sm text-neutral-500 bg-neutral-50 px-3 py-2 rounded border border-neutral-100 w-fit hover:text-primary cursor-pointer"
                  @click="downloadAction(tool.id, tool.title)"
                >
                  <i class="fa fa-file-archive-o mr-2 text-primary"></i>
                  <span>下载附件</span>
                </div>
              </div>

              <!-- 操作按钮 -->
              <div
                class="flex sm:flex-col justify-end sm:justify-center gap-3 border-t sm:border-t-0 sm:border-l border-neutral-100 pt-4 sm:pt-0 sm:pl-6"
              >
                <!-- 待审批状态：显示通过和拒绝 -->
                <template v-if="currentStatus === 0">
                  <button
                    @click="handleApprove(tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-check mr-2"></i>通过
                  </button>
                  <button
                    @click="handleReject(tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-times mr-2"></i>拒绝
                  </button>
                </template>

                <!-- 已上架状态：显示下架 -->
                <template v-if="currentStatus === 1">
                  <button
                    @click="handleReject(tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-arrow-down mr-2"></i>下架
                  </button>
                </template>

                <!-- 已拒绝状态：显示重新上架 -->
                <template v-if="currentStatus === 2">
                  <button
                    @click="handleApprove(tool)"
                    class="flex-1 sm:flex-none px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i class="fa fa-refresh mr-2"></i>重新上架
                  </button>
                </template>

                <button
                  @click="openDetail(tool)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-neutral-50 text-neutral-600 rounded-lg hover:bg-neutral-100 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-eye mr-2"></i>详情
                </button>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div class="flex justify-center mt-8">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page="page"
              @current-change="handlePageChange"
            />
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="bg-white rounded-xl border border-neutral-200 p-16 text-center"
        >
          <div
            class="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 text-neutral-400"
          >
            <i class="fa fa-check-circle-o text-3xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-2">
            暂无{{ getStatusText(currentStatus) }}任务
          </h3>
          <p class="text-neutral-500 text-sm">当前列表为空</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { globalState } from "@/state";
import { ElMessageBox, ElMessage } from "element-plus";

export default {
  name: "ExamPage",
  data() {
    return {
      currentStatus: 0, // 默认显示待审批
      tabs: [
        { label: "待审批", value: 0 },
        { label: "已上架", value: 1 },
        { label: "已拒绝", value: 2 },
      ],
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      categoryList: [],
    };
  },
  created() {
    this.getCategory();
    this.fetchData();
  },
  methods: {
    // 获取分类列表（用于显示分类名称）
    getCategory() {
      this.$axios({
        url: "category/list",
      }).then((res) => {
        if (res.code === 200) {
          this.categoryList = res.data;
        }
      });
    },

    // 切换状态
    changeStatus(status) {
      this.currentStatus = status;
      this.page = 1; // 切换状态时重置页码
      this.fetchData();
    },

    // 获取待审批列表
    async fetchData() {
      this.loading = true;
      try {
        // 构建请求数据
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          status: this.currentStatus, // 使用当前状态
        });

        const res = await this.$axios({
          url: "aitool/list/status", // 假设的审核列表接口
          method: "POST",
          data: { data },
        });

        if (res.code === 200) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        } else {
          // 如果接口不存在或报错，尝试使用 mock 数据（仅用于演示，实际应移除）
          console.warn("API 调用失败，使用 Mock 数据", res);
          // this.list = [];
          // this.total = 0;
        }
      } catch (error) {
        console.error("获取审批列表失败:", error);
        ElMessage.error("获取列表失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },

    // 翻页
    handlePageChange(val) {
      this.page = val;
      this.fetchData();
    },

    downloadAction(id, title) {
      this.$axios({
        url: "aitool/exam/download/" + id, // 注意：这里建议检查URL拼接是否正确，通常需要 / 分隔，比如 "aitool/download/" + tool.id
      })
        .then(async (res) => {
          if (res.code == 200) {
            const fileUrl = res.data.file;
            // 调用下载函数
            this.downloadFile(fileUrl, title);

            // 提示下载成功
            ElMessage({
              message: "下载已开始",
              type: "success",
            });
          } else {
            ElMessage({
              message: res.msg || "下载失败", // 注意：你原代码是 res.msh，应该是笔误，修正为 res.msg
              type: "error",
            });
          }
        })
        .catch((err) => {
          // 捕获请求异常
          ElMessage({
            message: "网络错误，下载失败",
            type: "error",
          });
          console.error("下载请求失败：", err);
        });
    },
    // 封装文件下载函数
    downloadFile(fileUrl, fileName) {
      // 创建a标签
      const link = document.createElement("a");
      // 设置下载地址
      link.href = fileUrl;
      // 设置下载文件名（可选，若无则使用服务器返回的文件名）
      if (fileName) {
        link.download = fileName;
      }
      // 隐藏a标签
      link.style.display = "none";
      // 添加到DOM
      document.body.appendChild(link);
      // 触发点击
      link.click();
      // 移除a标签
      document.body.removeChild(link);
    },
    // 审批通过
    handleApprove(tool) {
      const isReApprove = this.currentStatus === 2;
      const title = isReApprove ? "重新上架" : "通过审核";
      const confirmText = isReApprove
        ? `确定重新上架工具《${tool.title}》吗？`
        : `确定通过工具《${tool.title}》的审核吗？`;

      ElMessageBox.confirm(confirmText, title, {
        confirmButtonText: isReApprove ? "确定上架" : "确定通过",
        cancelButtonText: "取消",
        type: "success",
      })
        .then(async () => {
          try {
            const data = await this.$aesEncrypt({
              id: tool.id,
              status: 1, // 1 表示通过/上架
            });

            const res = await this.$axios({
              url: "aitool/exam",
              method: "PUT",
              data: { data },
            });

            if (res.code === 200) {
              ElMessage.success(isReApprove ? "工具已重新上架" : "审核已通过");
              this.fetchData(); // 刷新列表
            } else {
              ElMessage.error(res.msg || "操作失败");
            }
          } catch (error) {
            console.error(error);
            ElMessage.error("操作失败，请检查网络");
          }
        })
        .catch(() => {});
    },

    // 审批拒绝
    handleReject(tool) {
      const isOffShelf = this.currentStatus === 1;
      const title = isOffShelf ? "下架工具" : "拒绝审核";
      const promptText = isOffShelf ? "请输入下架理由" : "请输入拒绝理由";

      ElMessageBox.prompt(promptText, title, {
        confirmButtonText: isOffShelf ? "确定下架" : "确定拒绝",
        cancelButtonText: "取消",
        inputPattern: /\S/,
        inputErrorMessage: "理由不能为空",
      })
        .then(async ({ value }) => {
          try {
            const data = await this.$aesEncrypt({
              id: tool.id,
              status: 2, // 2 表示拒绝/下架
              examInfo: value,
            });

            const res = await this.$axios({
              url: "aitool/exam",
              method: "PUT",
              data: { data },
            });

            if (res.code === 200) {
              ElMessage.success(isOffShelf ? "工具已下架" : "已拒绝该申请");
              this.fetchData(); // 刷新列表
            } else {
              ElMessage.error(res.msg || "操作失败");
            }
          } catch (error) {
            console.error(error);
            ElMessage.error("操作失败，请检查网络");
          }
        })
        .catch(() => {});
    },

    // 打开详情
    openDetail(tool) {
      // 使用全局状态打开详情弹窗
      globalState.openModal(tool.id, this.categoryList);
    },

    // 辅助函数：获取状态文本
    getStatusText(status) {
      const map = {
        0: "待审批",
        1: "已上架",
        2: "已拒绝",
      };
      return map[status] || "未知状态";
    },

    // 辅助函数：获取分类名称
    getCategoryName(id) {
      const category = this.categoryList.find((c) => c.id === id);
      return category ? category.title : "未知分类";
    },

    // 辅助函数：格式化日期
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const h = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${d} ${h}:${min}`;
    },
  },
};
</script>

<style scoped>
/* 针对 Element Plus Pagination 的样式覆盖，使其更符合 Tailwind 风格 */
:deep(.el-pagination.is-background .el-pager li:not(.is-disabled).is-active) {
  background-color: var(--el-color-primary);
  color: white;
}
</style>
